import { useTranslations } from '@vocab/react';
import { Stack } from 'braid-design-system';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { PAGE_ID_SELECT_AD_TYPE } from 'src/app/constants/pageUid';
import {
  PRODUCT_SET_FAILED_UNAVAILABLE,
  PRODUCT_SET_SELECTED_PRODUCT_UNAVAILABLE,
} from 'src/app/constants/productSetStatus';
import { AdSelectionWrapper } from 'src/app/views/components/AdSelectionWrapper';
import Form from 'src/app/views/components/Form';
import { PageButtons } from 'src/app/views/components/PageButtons';
import { withDesktopEnhancers } from 'src/app/views/enhancers/withDesktopEnhancers';
import { useHandleEmailVerification } from 'src/app/views/hooks/useHandleEmailVerification';
import PageLayout from 'src/app/views/layouts/PageLayout';
import type AppState from 'src/types/appState';
import type { CreateFlowPageProps } from 'src/types/pages';

import translations from './.vocab';
import { SelectAdTypeError } from './components/SelectAdTypeError';

export const SelectAdType = (props: CreateFlowPageProps) => {
  const { t } = useTranslations(translations);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { productSetStatus, selectedProductId } = useSelector(
    (state: AppState) => ({
      productSetStatus: state.session.productSetStatus,
      selectedProductId: state.formData.selectedProductId,
    }),
  );

  const { handleEmailVerification } = useHandleEmailVerification();

  const handleValidation = async (skipIfNull = false) => {
    setIsLoading(true);

    await handleEmailVerification();

    setIsLoading(false);

    const message =
      !skipIfNull && !selectedProductId
        ? t('ad-type-page-validation-message')
        : null;

    setErrorMessage(message);

    return message;
  };

  const onAdProductSelected = (selectedAdProductId: string) => {
    if (Boolean(selectedAdProductId) && errorMessage !== null) {
      setErrorMessage(null);
    }
  };

  const isUnableToSelectExistingProduct =
    productSetStatus === PRODUCT_SET_SELECTED_PRODUCT_UNAVAILABLE;

  return (
    <PageLayout
      hasAside={false}
      hasProgressBar
      handleDispatchDraft={props.handleDispatchDraft}
      handleProgrammaticRouteChange={props.handleProgrammaticRouteChange}
      handleValidation={handleValidation}
      pageErrorMessage={props.pageErrorMessage}
    >
      <Stack space="medium">
        <Form
          handleNextPageRoute={props.handleNextPageRoute}
          handleValidation={handleValidation}
        >
          <Stack space="medium">
            {isUnableToSelectExistingProduct && (
              <SelectAdTypeError tone="info">
                {t('ad-type-page-alert-expired-text')}
              </SelectAdTypeError>
            )}
            <AdSelectionWrapper onAdProductSelected={onAdProductSelected} />
            {Boolean(errorMessage) && (
              <SelectAdTypeError marginBottom="small">
                {errorMessage}
              </SelectAdTypeError>
            )}
            {productSetStatus !== PRODUCT_SET_FAILED_UNAVAILABLE && (
              <PageButtons
                isLoading={isLoading}
                handleSaveDraftFailed={props.handleSaveDraftFailed}
                handleValidation={handleValidation}
              />
            )}
          </Stack>
        </Form>
      </Stack>
    </PageLayout>
  );
};

export default withDesktopEnhancers(PAGE_ID_SELECT_AD_TYPE)(SelectAdType);
